import { useCallback, useMemo } from 'react';
import { useClient } from 'urql';
import { makePath } from 'utils/routes';

import type {
  GetLinksQuery,
  GetLinksQueryVariables,
} from '../../hooks/customerApi';
import type { ActivePath } from './ActivePaths';
import type { NavTree } from './SideNav';
import { GetLinksDocument } from '../../hooks/customerApi';
import { useCustomerApiContext } from '../../hooks/useCustomerApiContext';
import { setActiveNavTreePath } from './SideNav';

export type IntegrationsNavTreeProps = {
  active: ActivePath;
  workspaceId: string;
  linkId?: string;
  externalAccountId?: string;
};
export const useIntegrationsNavTree = ({
  active,
  workspaceId,
  linkId,
  externalAccountId,
}: IntegrationsNavTreeProps): NavTree => {
  const client = useClient();
  const { context: customerApiContext } = useCustomerApiContext();

  const onLinksNavHover = useCallback(async () => {
    const variables = {};
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
    client
      .query<GetLinksQuery, GetLinksQueryVariables>(
        GetLinksDocument,
        variables,
        {
          ...customerApiContext,
          pause: !customerApiContext.token,
          requestPolicy: 'cache-first',
        }
      )
      .toPromise();
  }, [client, customerApiContext]);

  return useMemo(() => {
    const navTree: NavTree = {
      links: {
        props: {
          href: makePath(
            ['w', workspaceId, 'projects', 'settings', 'integrations'],
            {
              absolute: true,
            }
          ),
          children: 'Integrations',
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
        onHover: onLinksNavHover,
        visibleOverride: active === 'links' ? false : undefined,
        subNav: {
          accounts: {
            props: {
              href: linkId
                ? makePath(
                    [
                      'w',
                      workspaceId,
                      'projects',
                      'settings',
                      'integrations',
                      'links',
                      'id',
                      linkId,
                    ],
                    { absolute: true }
                  )
                : '',
              children: 'External Accounts',
            },
            visibleOverride: active === 'links.accounts' ? false : undefined,
            subNav: {
              transactions: {
                props: {
                  href:
                    linkId && externalAccountId
                      ? makePath(
                          [
                            'w',
                            workspaceId,
                            'projects',
                            'settings',
                            'integrations',
                            'links',
                            'id',
                            linkId,
                            'accounts',
                            externalAccountId,
                          ],
                          { absolute: true }
                        )
                      : '',
                  children: 'Transactions',
                },
              },
            },
          },
          detail: {
            props: {
              href: linkId
                ? makePath(
                    [
                      'w',
                      workspaceId,
                      'projects',
                      'settings',
                      'integrations',
                      'links',
                      'id',
                      linkId,
                      'details',
                    ],
                    { absolute: true }
                  )
                : '',
              children: 'Details',
            },
          },
        },
      },
    };
    setActiveNavTreePath(navTree, active);
    return navTree;
  }, [active, workspaceId, onLinksNavHover, linkId, externalAccountId]);
};
